import { useCallback, useEffect, useState } from "react"
import { NavLink } from "react-router-dom"

import NotificationUnreadBadge from "@/components/notifications/NotificationUnreadBadge"
import { useAuthContext } from "@/contexts/AuthContext"
import { notificationService } from "@/services/notification.service"
import {
    countUnreadNotifications,
    type NotificationListScope,
} from "@/utils/notificationDisplay"

export type SidebarMenuItem = {
    label: string
    path: string
    icon: React.ComponentType<{ size?: number; className?: string }>
    end?: boolean
}

type BaseSidebarProps = {
    title: string
    items: SidebarMenuItem[]
    notificationBadgePath?: string
    notificationScope?: NotificationListScope
}

const BaseSidebar = ({
    title,
    items,
    notificationBadgePath,
    notificationScope = "mine",
}: BaseSidebarProps) => {
    const { user } = useAuthContext()
    const [unreadCount, setUnreadCount] = useState(0)

    const syncUnreadNotifications = useCallback(async () => {
        if (!notificationBadgePath || !user?.userId) {
            setUnreadCount(0)
            return
        }

        try {
            const items =
                notificationScope === "admin"
                    ? await notificationService.getAll()
                    : await notificationService.getMine()
            setUnreadCount(countUnreadNotifications(items))
        } catch {
            // Badge is best-effort.
        }
    }, [notificationBadgePath, notificationScope, user?.userId])

    useEffect(() => {
        void syncUnreadNotifications()
    }, [syncUnreadNotifications])

    useEffect(() => {
        if (!notificationBadgePath || !user?.userId) return

        const handleSync = () => {
            void syncUnreadNotifications()
        }

        window.addEventListener("focus", handleSync)
        window.addEventListener("notifications:updated", handleSync)

        return () => {
            window.removeEventListener("focus", handleSync)
            window.removeEventListener("notifications:updated", handleSync)
        }
    }, [notificationBadgePath, syncUnreadNotifications, user?.userId])

    return (
        <aside className="w-fit min-w-[230px] bg-white/70 backdrop-blur-xl border-r border-white/40 pt-24 px-2">
            <p className="px-3 mb-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                {title}
            </p>

            <nav className="flex flex-col gap-1">
                {items.map((item) => {
                    const Icon = item.icon

                    return (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            end={item.end}
                            className={({ isActive }) =>
                                `flex w-full items-center gap-3 px-3 h-10 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${isActive
                                    ? "bg-gradient-to-r from-green-400 to-emerald-500 text-white shadow"
                                    : "text-gray-600 hover:bg-white hover:shadow-sm"
                                }`
                            }
                        >
                            {({ isActive }) => {
                                const showNotificationBadge =
                                    Boolean(notificationBadgePath) &&
                                    item.path === notificationBadgePath

                                return (
                                    <>
                                        <Icon
                                            size={18}
                                            className={
                                                isActive ? "text-white" : "text-gray-500"
                                            }
                                        />
                                        <span className="flex-1">{item.label}</span>
                                        {showNotificationBadge ? (
                                            <NotificationUnreadBadge
                                                count={unreadCount}
                                                inline
                                                onAccent={isActive}
                                            />
                                        ) : null}
                                    </>
                                )
                            }}
                        </NavLink>
                    )
                })}
            </nav>
        </aside>
    )
}

export default BaseSidebar
